import { useEffect } from 'react'
import { useLatestRef } from './useLatestRef'

const SLOP_PX = 10

/**
 * Calls `onDismiss` once the user starts scrolling the page while `active`, so
 * a popover doesn't hang over content sliding under it. Listens to the gesture
 * (touch drag, wheel) rather than `scroll`: scroll restore and anchoring move
 * the window on their own and must not close a popover they're restoring.
 * Gestures that start inside `insideSelector` (the popover itself) are ignored.
 */
export function useDismissOnScroll(active: boolean, onDismiss: () => void, insideSelector?: string) {
  const onDismissRef = useLatestRef(onDismiss)
  useEffect(() => {
    if (!active) return
    let done = false
    let startY: number | null = null
    const inside = (e: Event) =>
      !!insideSelector && e.target instanceof Element && !!e.target.closest(insideSelector)
    const dismiss = () => {
      if (done) return
      done = true
      onDismissRef.current()
    }
    const onTouchStart = (e: TouchEvent) => {
      startY = inside(e) ? null : e.touches[0].clientY
    }
    const onTouchMove = (e: TouchEvent) => {
      if (startY !== null && Math.abs(e.touches[0].clientY - startY) > SLOP_PX) dismiss()
    }
    const onWheel = (e: WheelEvent) => {
      if (!inside(e)) dismiss()
    }
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: true })
    window.addEventListener('wheel', onWheel, { passive: true })
    return () => {
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('wheel', onWheel)
    }
  }, [active, insideSelector, onDismissRef])
}
